/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { FancyActionableFirebase } from 'src/app/common/fancy-firebase/generics/FancyActionableFirebase';
import { PlanList } from 'src/app/common/fancy-firebase/services/factories';
import { DefaultSplitDataRenderType, Plan, PlanFields } from '@touchplan/common';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { dateCache } from '@touchplan/common';
import { PlanAccessApi } from './PlanAccessApi';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';

export class PlansApi extends FancyActionableFirebase<Plan, FancyAction, PlanList> {
	constructor(public data: PlanList, public planAccessApi?: PlanAccessApi) {
		super(data);
	}

	public addPlan(data: PlanFields): void {
		// TODO add planAccess check

		const key = this.data.generateNewChildKey();
		const planObj = Object.assign(
			{
				planId: key,
				activeLineX: 1000,
				activeLineDate: fbdbTime.syncMoment.add(2, 'weeks').format('YYYY-MM-DD'),
				createdAt: fbdbTime.syncTime,
				targetStartDate: data.targetStartDate ? dateCache.getFormat(data.targetStartDate) : null,
				targetFinishDate: data.targetFinishDate ? dateCache.getFormat(data.targetFinishDate) : null,
			},
			data
		);

		// this.addItem(planObj, 'planId');
		this.addItemLegacy(planObj, key);

		if (!!data.planAccess) {
			this.planAccessApi?.updatePlanAccessLevel(
				key,
				data.planAccess.accessLevel,
				data.planAccess.excludeFromDashboard
			);
		}

		analyticsEventsService.createdAPlan(key, data.name);
	}

	public copyPlan(data: PlanFields): void {
		data.createdAt = fbdbTime.syncTime; // overwrite the creation date
		this.addItem(data, 'planId');
	}

	public lockPlan(planId: string, lock: boolean) {
		if (!this.planAccessApi) {
			return;
		}
		if (lock) {
			this.planAccessApi.lockPlan(planId);
		} else {
			this.planAccessApi.activatePlan(planId);
		}
	}

	// dev note: client doesn't delete plans, we simply add a prop "deletedAt" to plan with time stamp for fbdb to pick up and do all the remove/cleanup
	public removePlan(plan: Plan): void {
		plan.view.deletedAt = fbdbTime.syncTime;
		this.persistData(plan);
	}

	public setPullTicketSizeOnPlan(plan: Plan, pullSize: number): void {
		if (pullSize !== plan.view.pullTicketWidth) {
			const dataObject = { pullTicketWidth: pullSize };
			this.localUpdate(plan.$id, dataObject);
			this.persistData(plan, DefaultSplitDataRenderType.database);
		}
	}

	/**
	 * Writes data to the `projectId/planId/activatePullColumn`, which informs Firebase of the pull column to activate
	 * as part of an active line drag operation. This used to be a direct Firebase write in the old drag handler
	 * and was moved to the PlansApi instead.
	 * @param plan
	 * @param activatePullColumn
	 */
	public setActivatedPullColumn(plan: Plan, activatePullColumn: number): void {
		// dev note: client doesn't activate pull columns, we simply add a prop "activatePullColumn" to plan data
		// with the pull column index to activate and fbdb flings some spells at the sky and poof, activation.
		this.localUpdate(plan.$id, { activatePullColumn });
		this.persistData(plan, DefaultSplitDataRenderType.database);
	}

	/**
	 * Writes data to the `plans/projectId/planId/promisePeriodPosition` pointer which is used to mark the "next" promise period
	 * @param plan
	 * @param promisePeriodPosition
	 */
	public setPromisePeriodPosition(plan: Plan, promisePeriodPosition: string): void {
		this.localUpdate(plan.$id, { promisePeriodPosition });
		this.persistData(plan, DefaultSplitDataRenderType.database);
	}
}
